import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import TypeSearch from '../../components/common/TypeSearch';
import BranchSelectModal from '../../components/common/BranchSelectModal';

export default function QuotationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, branchId } = useAuth();
  const isEdit = !!id;
  const username = user?.email?.split('@')[0] || 'unknown';

  const [form, setForm] = useState({
    client_id: '', client_label: '',
    item_type: 'ticket',
    currency_id: '',
    details: '',
    valid_until: '',
    notes: '',
    op_data: {},
  });
  const [lineItems, setLineItems] = useState([{ type: 'Adult', rate: '', qty: '1' }]);
  const totalAmount = lineItems.reduce((sum, l) => sum + (parseFloat(l.rate) || 0) * (parseInt(l.qty) || 0), 0);
  const totalPax = lineItems.reduce((sum, l) => sum + (parseInt(l.qty) || 0), 0);
  const [currencies, setCurrencies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [branchConfirmed, setBranchConfirmed] = useState(false);

  useEffect(() => { if (branchId && !selectedBranchId) setSelectedBranchId(branchId); }, [branchId]);

  useEffect(() => {
    supabase.from('currencies').select('id, code, name').order('code').then(({ data }) => {
      setCurrencies(data || []);
      if (!isEdit) { const lkr = (data || []).find(c => c.code === 'LKR'); if (lkr) set('currency_id', lkr.id); }
    });
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name').then(({ data }) => setBranches(data || []));
    if (isEdit) {
      supabase.from('quotations').select('*, clients(full_name, client_code), currency:currency_id(code)').eq('id', id).single()
        .then(({ data }) => {
          if (data) {
            setForm({
              client_id: data.client_id || '',
              client_label: data.clients ? `${data.clients.full_name} (${data.clients.client_code})` : '',
              item_type: data.item_type || 'ticket',
              currency_id: data.currency_id || '',
              details: data.details || '',
              valid_until: data.valid_until || '',
              notes: data.notes || '',
              op_data: data.op_data || {},
            });
            const saved = data.op_data?.line_items;
            if (saved?.length) {
              setLineItems(saved.map(l => ({ type: l.type || 'Adult', rate: String(l.unitPrice || ''), qty: String(l.qty || '1') })));
            } else if (data.amount_per_pax) {
              setLineItems([{ type: 'Adult', rate: String(data.amount_per_pax), qty: String(data.num_pax || 1) }]);
            }
          }
        });
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setOp = (k, v) => setForm(f => ({ ...f, op_data: { ...f.op_data, [k]: v } }));

  // When a package is linked, pull its rate-card selling price for the current pax count
  // (still editable afterward) — re-runs whenever the pax count changes while linked.
  useEffect(() => {
    const packageId = form.op_data.package_id;
    if (!packageId || totalPax <= 0) return;
    let cancelled = false;
    supabase.from('package_selling_prices')
      .select('sell_price_per_pax, currency_id')
      .eq('package_id', packageId)
      .lte('pax_from', totalPax)
      .or(`pax_to.gte.${totalPax},pax_to.is.null`)
      .limit(1)
      .then(({ data }) => {
        if (cancelled || !data?.length) return;
        const tier = data[0];
        setLineItems(ls => ls.map((l, i) => i === 0 ? { ...l, rate: String(tier.sell_price_per_pax) } : l));
        if (tier.currency_id) set('currency_id', tier.currency_id);
      });
    return () => { cancelled = true; };
  }, [form.op_data.package_id, totalPax]); // eslint-disable-line

  const handleSubmit = async () => {
    if (!form.client_id) { setError('Select a client'); return; }
    if (totalAmount <= 0) { setError('Enter at least one line item with a rate.'); return; }
    if (!form.currency_id) { setError('Select a currency'); return; }
    if (form.item_type === 'package' && !form.op_data.package_mode) { setError('Choose Group or FIT for this package pro forma.'); return; }
    if (form.item_type === 'package' && form.op_data.package_mode === 'group' && !form.op_data.package_id) { setError('Group package pro formas must be linked to a package.'); return; }
    setSaving(true); setError('');
    try {
      const builtLines = lineItems
        .filter(l => parseFloat(l.rate) > 0 && parseInt(l.qty) > 0)
        .map(l => ({ type: l.type, unitPrice: parseFloat(l.rate), qty: parseInt(l.qty), amount: parseFloat(l.rate) * parseInt(l.qty) }));
      const payload = {
        client_id: form.client_id,
        item_type: form.item_type,
        num_pax: totalPax,
        amount_per_pax: builtLines[0]?.unitPrice || 0,
        total_amount: totalAmount,
        currency_id: form.currency_id,
        details: form.details || null,
        valid_until: form.valid_until || null,
        notes: form.notes || null,
        op_data: { ...form.op_data, line_items: builtLines },
      };
      if (isEdit) {
        await supabase.from('quotations').update({ ...payload, updated_by: username, updated_at: new Date().toISOString() }).eq('id', id);
        navigate(`/quotations/${id}?print=1`);
      } else {
        // Generate PRO-YY-#### with MAX seq
        const yy = String(new Date().getFullYear()).slice(-2);
        const prefix = `PRO-${yy}`;
        const { data: existing } = await supabase.from('quotations').select('quotation_number').ilike('quotation_number', `${prefix}-%`);
        const maxSeq = (existing || []).reduce((max, r) => {
          const seq = parseInt(r.quotation_number.split('-').pop()) || 0;
          return seq > max ? seq : max;
        }, 0);
        const quotation_number = `${prefix}-${String(maxSeq + 1).padStart(4, '0')}`;
        const { data, error: err } = await supabase.from('quotations')
          .insert({ ...payload, quotation_number, status: 'DRAFT', created_by: username, branch_id: selectedBranchId || null }).select().single();
        if (err) throw err;
        navigate(`/quotations/${data.id}?print=1`);
      }
    } catch (e) { setError(e.message || 'Error saving'); setSaving(false); }
  };

  return (
    <div>
      {!branchConfirmed && !isEdit && (
        <BranchSelectModal
          defaultBranchId={selectedBranchId || branchId}
          onConfirm={(id) => { setSelectedBranchId(id); setBranchConfirmed(true); }}
          onCancel={() => navigate(-1)}
        />
      )}
      <div className="page-header">
        <div><div className="page-title">{isEdit ? 'Edit Pro Forma Invoice' : 'New Pro Forma Invoice'}</div></div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <label className="form-label">Branch</label>
            <select className="form-input" style={{ minWidth: 140 }} value={selectedBranchId} onChange={e => setSelectedBranchId(e.target.value)}>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Save & Print'}</button>
          </div>
        </div>
      </div>

      {error && <div style={{ color: 'var(--danger)', marginBottom: 16 }}>{error}</div>}

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 18, padding: 24 }}>

        {/* Client */}
        <div className="form-group">
          <label className="form-label">Client *</label>
          <TypeSearch
            fetchFn={async (q) => {
              let qb = supabase.from('clients').select('id, full_name, client_code').eq('is_active', true).limit(40);
              if (q) qb = qb.or(`full_name.ilike.%${q}%,client_code.ilike.%${q}%`);
              else qb = qb.order('full_name');
              const { data } = await qb;
              return (data || []).map(c => ({ ...c, label: `${c.full_name} (${c.client_code})` }));
            }}
            value={form.client_label}
            displayKey="label"
            placeholder="Search client…"
            onSelect={c => { set('client_id', c.id); set('client_label', c.label); }}
            onClear={() => { set('client_id', ''); set('client_label', ''); }}
          />
        </div>

        {/* Item type + currency */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">Item Type *</label>
            <select className="form-input" value={form.item_type} onChange={e => { set('item_type', e.target.value); set('op_data', {}); }}>
              <option value="ticket">Ticket</option>
              <option value="visa">Visa</option>
              <option value="package">Package</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Currency *</label>
            <select className="form-input" value={form.currency_id} onChange={e => set('currency_id', e.target.value)}>
              <option value="">— Select —</option>
              {currencies.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
            </select>
          </div>
        </div>

        {/* Line Items */}
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 8, marginBottom: 6 }}>
            {['Type','Rate','Qty','Subtotal',''].map(h => (
              <div key={h} style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</div>
            ))}
          </div>
          {lineItems.map((li, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              {li.type === 'Other' || (!['Adult','Child','Infant'].includes(li.type) && li.type !== '') ? (
                <input className="form-input" placeholder="Describe type…" autoFocus
                  value={['Adult','Child','Infant'].includes(li.type) ? '' : li.type === 'Other' ? '' : li.type}
                  onChange={e => setLineItems(ls => ls.map((r, idx) => idx === i ? { ...r, type: e.target.value } : r))}
                  onBlur={e => { if (!e.target.value) setLineItems(ls => ls.map((r, idx) => idx === i ? { ...r, type: 'Adult' } : r)); }} />
              ) : (
                <select className="form-input" value={li.type}
                  onChange={e => setLineItems(ls => ls.map((r, idx) => idx === i ? { ...r, type: e.target.value } : r))}>
                  <option value="Adult">Adult</option>
                  <option value="Child">Child</option>
                  <option value="Infant">Infant</option>
                  <option value="Other">Other…</option>
                </select>
              )}
              <input className="form-input" type="number" placeholder="0.00" min="0" step="0.01"
                value={li.rate} onChange={e => setLineItems(ls => ls.map((r, idx) => idx === i ? { ...r, rate: e.target.value } : r))} />
              <input className="form-input" type="number" placeholder="1" min="1"
                value={li.qty} onChange={e => setLineItems(ls => ls.map((r, idx) => idx === i ? { ...r, qty: e.target.value } : r))} />
              <div className="form-input" style={{ background: 'var(--bg-input)', fontWeight: 600, fontSize: 13 }}>
                {((parseFloat(li.rate)||0)*(parseInt(li.qty)||0)).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
              </div>
              <button className="btn btn-secondary btn-sm" disabled={lineItems.length === 1}
                onClick={() => setLineItems(ls => ls.filter((_, idx) => idx !== i))}>✕</button>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setLineItems(ls => [...ls, { type: 'Adult', rate: '', qty: '1' }])}>+ Add Row</button>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Total: {totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>

        {/* Operational fields by type */}
        {form.item_type === 'ticket' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Airline</label>
              <input className="form-input" value={form.op_data.airline || ''} onChange={e => setOp('airline', e.target.value)} placeholder="e.g. Emirates" />
            </div>
            <div className="form-group">
              <label className="form-label">PNR</label>
              <input className="form-input" value={form.op_data.pnr || ''} onChange={e => setOp('pnr', e.target.value)} placeholder="6-char PNR" />
            </div>
            <div className="form-group">
              <label className="form-label">Route (From → To)</label>
              <input className="form-input" value={form.op_data.route || ''} onChange={e => setOp('route', e.target.value)} placeholder="CMB → DXB" />
            </div>
            <div className="form-group">
              <label className="form-label">Travel Date</label>
              <input type="date" className="form-input" value={form.op_data.travel_date || ''} onChange={e => setOp('travel_date', e.target.value)} />
            </div>
          </div>
        )}

        {form.item_type === 'visa' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Country</label>
              <input className="form-input" value={form.op_data.country || ''} onChange={e => setOp('country', e.target.value)} placeholder="e.g. UAE" />
            </div>
            <div className="form-group">
              <label className="form-label">Visa Type</label>
              <input className="form-input" value={form.op_data.visa_type || ''} onChange={e => setOp('visa_type', e.target.value)} placeholder="e.g. Tourist" />
            </div>
          </div>
        )}

        {form.item_type === 'package' && (
          <div className="form-group">
            <label className="form-label">Package Type *</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['group', 'fit'].map(m => (
                <button key={m} type="button"
                  onClick={() => { setOp('package_mode', m); if (m === 'fit') { setOp('package_id', ''); setOp('package_label', ''); } }}
                  className={`btn btn-sm ${form.op_data.package_mode === m ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ textTransform: 'uppercase' }}>
                  {m}
                </button>
              ))}
            </div>
          </div>
        )}

        {form.item_type === 'package' && (
          <div className="form-group">
            <label className="form-label">Link to Package{form.op_data.package_mode === 'group' ? ' *' : ' (optional)'}</label>
            <TypeSearch
              fetchFn={async (q) => {
                let qb = supabase.from('packages').select('id, package_name, package_code').eq('is_active', true).ilike('package_name', `%${q}%`);
                if (form.op_data.package_mode === 'group') qb = qb.eq('package_type', 'Group');
                const { data } = await qb.limit(20);
                return (data || []).map(p => ({ ...p, label: `${p.package_name} (${p.package_code})` }));
              }}
              value={form.op_data.package_label || ''}
              displayKey="label"
              placeholder="Search package…"
              onSelect={p => { setOp('package_id', p.id); setOp('package_label', p.label); }}
              onClear={() => { setOp('package_id', ''); setOp('package_label', ''); }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              {form.op_data.package_mode === 'group'
                ? 'Required for Group — pulls the selling price for this pax count from the package\'s rate card (still editable below), and its real supplier costs pull in too once converted to an invoice.'
                : 'Pulls the selling price for this pax count from the package\'s rate card (still editable below). If this pro forma is later converted to an invoice, the package\'s real supplier costs pull in too. Leaving it unconverted has no effect on the books.'}
            </div>
          </div>
        )}

        {form.item_type === 'package' && (
          <div className="form-group">
            <label className="form-label">Link to Booking (optional)</label>
            <TypeSearch
              fetchFn={async (q) => {
                const { data } = await supabase.from('bookings').select('id, booking_ref, clients(full_name)').ilike('booking_ref', `%${q}%`).limit(20);
                return (data || []).map(b => ({ ...b, label: `${b.booking_ref} — ${b.clients?.full_name}` }));
              }}
              value={form.op_data.booking_label || ''}
              displayKey="label"
              placeholder="Search booking ref…"
              onSelect={b => setOp('booking_id', b.id) || setOp('booking_label', b.label)}
              onClear={() => { setOp('booking_id', ''); setOp('booking_label', ''); }}
            />
          </div>
        )}

        {/* Details */}
        <div className="form-group">
          <label className="form-label">Details / What's Included</label>
          <textarea className="form-input" rows={3} value={form.details}
            onChange={e => set('details', e.target.value)} placeholder="Describe what is included in this quotation…" />
        </div>

        {/* Valid until + notes */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">Valid Until</label>
            <input type="date" className="form-input" value={form.valid_until} onChange={e => set('valid_until', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>

      </div>
    </div>
  );
}
